<!--
    ONE insured object as a card: a titled header band, the level-1 figures, and one expander for
    level 2. Chrome, spacing and colour are PrimeFlex utilities and theme tokens.
-->
@if (object) {

    <!--
        overflow-hidden lets the header band run edge to edge inside the rounded border — the band
        carries no radius of its own and the card clips its top corners. Same construction as the
        Deckung and Person cards, so the three read as one family.
    -->
    <div class="surface-card border-1 surface-border border-round overflow-hidden">

        <!--
            The header BAND. `surface-100` — the sheet's own subtle section background, never a new
            colour — plus the card's one divider, so the designation reads apart from the figures.

            Same contract as the Deckung card, minus its accent spine: objects are not grouped by
            Sparte, so there is no family to colour by and a bar would state a grouping that does not
            exist.
        -->
        <div class="flex align-items-baseline justify-content-between gap-2 surface-100 border-bottom-1 surface-border"
             [ngClass]="headClass">

            <!--
                THE ellipsis. The designation of an object is a partner-supplied string of unbounded
                length ("Einfamilienhaus, Musterstraße 14"), and it must be cut, never wrapped: a
                two-line heading in one card pushes the figures of every card beside it out of
                alignment.

                min-w-0 is load-bearing — a flex child defaults to min-width:auto and refuses to shrink
                below its content, so text-overflow-ellipsis alone does nothing here. title keeps the
                full heading reachable on hover.

                `text-color` and not the emphasis colour, for the same reason as on the sibling cards:
                a dozen titles in the accent colour is a highlighter, not a hierarchy.

                THE DESIGNATION CAN BE THE HIT — "Einfamilienhaus, Musterstraße 14" is exactly the kind
                of string a clerk searches for. It is counted into the "{n} Treffer" beside it, and the
                marker used to be the only clue that anything on this card matched. `.cm-ed-hit-title`
                is the sheet's one treatment for "the term is in this heading", worn identically by the
                Deckung card, the person card, the section heading and every row heading.

                The mark sits on an INNER span: the outer element is a flex item with `flex-1` and owns
                the free width of the band, so washing it would paint a bar across the empty space
                beside a short designation. The ellipsis is unaffected — `text-overflow` works on the
                outer block's overflowing line box regardless of how its inline content is nested.
            -->
            <span class="font-semibold text-color flex-1 min-w-0 overflow-hidden text-overflow-ellipsis white-space-nowrap"
                  [ngClass]="titleClass"
                  [title]="object.title"><span [class.cm-ed-hit-title]="ownTextHit">{{object.title}}</span></span>

            <!--
                The object-kind line is dropped at xs — now BELOW 560 px, where the old ladder put that
                floor at 420 px: in a card that narrow it takes the width the heading needs, and the
                heading is what identifies the card. It stays pinned to the FLOOR of the ladder and is
                not allowed to creep up it: from `sm` on the card is col-12, i.e. the widest card on
                the whole ladder, and both lines fit with room over.
            -->
            <!--
                Matched together with the title (`countTextHits(title, sub)` publishes one total), so
                it takes the same mark: the screen states "the term is in this card's own text" and
                never guesses which half of it carried the term. Inner span, as everywhere — the muted
                utility stays on the outer element and the mark's own colour still wins, which it must,
                since a marked sub that kept the muted tone measures 3.58:1 on the hit wash against the
                body colour's 7.78:1.
            -->
            @if (object.sub && size !== 'xs') {
                <span class="text-xs text-color-secondary white-space-nowrap"><span [class.cm-ed-hit-title]="ownTextHit">{{object.sub}}</span></span>
            }

            <!--
                WHAT THIS CARD HOLDS FOR THE CURRENT TERM. Nothing on this sheet dims or disappears
                during a search, so a card with matches inside its collapsed level-2 block looks
                exactly like one with none — this marker is the only thing on it that says otherwise.

                Only while a term is active, and only when there is something to declare. NOT dropped
                at `xs`, unlike the object-kind line beside it: that is context a narrow card can
                spare, while an unread hit is the thing the reader came for.
            -->
            @if (searching && object.hitCount) {
                <span class="cm-ed-hits bg-primary-50 text-primary border-1 border-primary border-round-sm white-space-nowrap">{{object.hitCount}} Treffer</span>
            }

        </div>

        <!-- The card's body. -->
        <div [ngClass]="padClass">

            <!--
                BASISDATEN — ONE renderer for every pair on this screen (see mvs-cm-ed-kv-list), and
                the level is stated rather than left to the default: this card holds two lists of the
                same shape, and they are exactly where the two levels have to be told apart.
            -->
            @if (object.level1.length) {
                <mvs-cm-ed-kv-list [fields]="object.level1"
                                   [size]="size"
                                   emphasis="basis"
                                   [searching]="searching"></mvs-cm-ed-kv-list>
            }

            <!--
                The expander. A p-button and not a clickable div, so Space and Enter activate it, it is
                reachable by keyboard and it carries the theme's own focus ring. Rendered ONLY when there is
                something behind it — a dead "0 weitere Angaben" is worse than no control at all.

                No `severity="secondary"`: it is an ACTION and wears the theme's primary colour, like the
                same control on the Deckung and Person cards.

                aria-expanded / aria-controls travel through [pt].root, which is the only way onto the
                element that actually carries the button role. The top margin is the PrimeFlex utility and
                not a SCSS declaration: p-button forwards styleClass onto that same element.

                The LABEL follows `open`, not `expanded`: while a term is active the block may already be
                on screen because the search put it there, and "3 weitere Angaben →" over visible pairs is
                a control that lies about what it does.
            -->
            @if (object.level2.length) {
                <p-button [label]="open ? 'Weniger anzeigen' : object.level2.length + ' weitere Angaben →'"
                          size="small"
                          [text]="true"
                          styleClass="cm-ed-more mt-1"
                          [pt]="foldPt"
                          (onClick)="toggle()"></p-button>
            }

            <!--
                The controlled region is ALWAYS in the document and only its content is conditional: an
                aria-controls pointing at an element that does not exist yet is a dangling reference for
                assistive technology.

                WEITERE ANGABEN, and it says so: the run label names the level and the emphasis step
                (lighter weight, same tone) shows it, so what the expander reveals is visibly not more
                basics. The label carries its own rule, which is why the old `pt-2 border-top-1` is
                gone — a divider and a labelled rule 4 px apart is two boundaries for one boundary.

                AUTO-REVEAL. `open` is the reader's own state OR, while a term is active, the search
                having found one of its matches inside these very pairs. A match behind a closed
                expander is a match the reader is never shown — and with nothing dimmed, an unopened
                card would look exactly like a card with nothing in it. Temporary, never written back.
            -->
            <div [attr.id]="level2Id">
                @if (open) {
                    <div class="mt-1">
                        <mvs-cm-ed-kv-list [fields]="object.level2"
                                           [size]="size"
                                           emphasis="weitere"
                                           runLabel="Weitere Angaben"
                                           [searching]="searching"></mvs-cm-ed-kv-list>
                    </div>
                }
            </div>

        </div>

    </div>
}
